/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br."Knowledge
 * for Decisions (K4D)" and the slogan"Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from"react";
import { createPortal } from"react-dom";
import { cn } from"./Select.helpers";
import { dropdownPanelStyle, type DropdownPosition } from"./use-dropdown-position";

interface TriggerButtonProps {
  state: any;
  disabled: boolean;
  multiple: boolean;
  clearable: boolean;
  rawValue: string | null | undefined;
  rawPlaceholder?: string;
  labelText: string;
  leftSection?: React.ReactNode;
  idProp?: string;
  name: string;
  errorBorder: string;
  restProps: Record<string, any>;
  handleTriggerKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  handleClear: (e: React.MouseEvent) => void;
  t: (k: string, o?: any) => string;
}

export const TriggerButton: React.FC<TriggerButtonProps> = ({
  state, disabled, multiple, clearable, rawValue, rawPlaceholder, labelText, leftSection,
  idProp, name, errorBorder, restProps, handleTriggerKeyDown, handleClear, t,
}) => (
  <button type="button" onClick={() => !disabled && state.toggleOpen()} onKeyDown={handleTriggerKeyDown} disabled={disabled}
    className={cn(
      "flex min-h-[44px] w-full items-center justify-between rounded-(--radius-md) border bg-(--surface-secondary) text-(--text-primary) px-3 py-2 text-sm",
      "ring-offset-background placeholder:text-(--text-tertiary)",
      "transition-shadow duration-150",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--input-focus-ring) focus-visible:ring-offset-0 focus-visible:shadow-[0_0_0_3px_var(--input-focus-shadow)]",
      "disabled:cursor-not-allowed disabled:opacity-50",
      errorBorder,
    )}
    id={idProp} name={name} {...restProps}>
    <div className="flex items-center gap-2 flex-1 min-w-0">
      {leftSection && <span className="flex-shrink-0">{leftSection}</span>}
      <span className={cn("truncate", !(multiple ? state.multiDisplayValue : state.displayValue) &&"text-[var(--text-tertiary)]")}>
        {(multiple ? state.multiDisplayValue : state.displayValue) || rawPlaceholder || labelText}
      </span>
    </div>
    <div className="flex items-center gap-1 flex-shrink-0">
      {clearable && (rawValue ?? state.selectedValue) && (
        <span role="button" tabIndex={0} onClick={handleClear}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClear(e as any); }}
          className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] cursor-pointer"
          aria-label={t('form.aria.clearSelection')}>×</span>
      )}
      <svg className={cn("h-4 w-4 text-[var(--text-tertiary)] transition-transform", state.isOpen &&"rotate-180")}
        fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  </button>
);

interface OptionsListProps {
  state: any;
  multiple: boolean;
  isMobile: boolean;
  singleValue: string;
  nothingFoundMessage?: string;
  renderOption?: (optionValue: string, selectedValue: string, isActive: boolean) => React.ReactNode;
  optionRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>;
  t: (k: string, o?: any) => string;
}

export const OptionsList: React.FC<OptionsListProps> = ({
  state, multiple, isMobile, singleValue, nothingFoundMessage, renderOption, optionRefs, t,
}) => {
  const selectedOnDown = React.useRef(false);
  return (
  <div className={cn(isMobile ?"py-2" :"p-1")}>
    {state.filteredData.length === 0 ? (
      <div className={cn("px-2 py-2 text-sm text-[var(--text-tertiary)]", isMobile &&"px-4 py-3 text-base")}>
        {nothingFoundMessage || t('form.messages.nothingFound')}
      </div>
    ) : state.filteredData.map((option: any, index: number) => {
      const isRowSelected = multiple
        ? state.selectedValues.includes(option.value)
        : (singleValue || state.selectedValue) === option.value;
      const isRowActive = index === state.activeOptionIndex;
      const showCheck = isRowSelected && (multiple || isMobile);
      const customLabel = renderOption ? renderOption(option.value, state.selectedValue, isRowActive) : null;
      return (
        <button key={option.value} ref={(el) => { optionRefs.current[index] = el; }} type="button"
          onMouseDown={(e) => { e.preventDefault(); selectedOnDown.current = true; state.onSelect(option.value); }}
          onClick={() => { if (!selectedOnDown.current) state.onSelect(option.value); selectedOnDown.current = false; }}
          className={cn(
            "w-full text-left text-sm hover:bg-[var(--surface-hover)]",
            isRowActive &&"bg-[var(--selection-active)]",
            !isRowActive && isRowSelected &&"bg-[var(--selection-active)]",
            // Desktop rows sit inside the panel's 4px inset, so px-2 lands the
            // label on the same 12px gutter as the trigger's own text.
            isMobile ?"px-4 py-3.5 text-base flex items-center justify-between rounded-(--radius-md)" :"px-2 py-2 flex items-center justify-between rounded-(--radius-sm)"
          )}>
          <span className="flex items-center gap-2 min-w-0">{customLabel ?? <span className="truncate">{option.label}</span>}</span>
          {showCheck && (
            <svg data-testid="option-check" className="w-5 h-5 text-[var(--seed-base)] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
      );
    })}
  </div>
  );
};

interface SearchInputProps {
  state: any;
  isMobile: boolean;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  handleSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  t: (k: string, o?: any) => string;
}

export const SearchInput: React.FC<SearchInputProps> = ({ state, isMobile, searchInputRef, handleSearchKeyDown, t }) => (
  <div className={cn("p-2 border-b border-[var(--border-subtle)]", isMobile &&"p-3")}>
    <input ref={searchInputRef} type="text" value={state.searchTerm}
      onChange={(e) => state.handleSearchTermChange(e.target.value)}
      onKeyDown={handleSearchKeyDown} placeholder={t('form.placeholders.search')}
      className={cn(
        "w-full px-2 text-sm border border-[var(--border-medium)]",
        "bg-[var(--surface-primary)] text-[var(--text-primary)]",
        "focus:outline-none focus:ring-2 focus:ring-[var(--seed-base)]",
        "",
        isMobile ?"h-11 text-base rounded-(--radius-md)" :"h-8"
      )} />
  </div>
);

/**
 * Renders the desktop dropdown into a fixed-position portal below the trigger,
 * returning null when closed, on mobile, or before position is measured.
 *
 * @param state Select open state.
 * @param isMobile Whether the mobile overlay is active instead.
 * @param pos The measured top/left/width for the dropdown, or null.
 * @param searchable Whether the search input should be shown.
 * @param searchInput The search input node.
 * @param optionsList The rendered options list node.
 * @returns The portal element, or null.
 */
export function renderDesktopDropdown(
  state: any, isMobile: boolean,
  pos: DropdownPosition | null,
  searchable: boolean, searchInput: React.ReactNode, optionsList: React.ReactNode,
) {
  if (!state.isOpen || isMobile || !pos) return null;
  return createPortal(
    <div data-select-portal
      onMouseDown={(e) => e.preventDefault()}
      // Chrome derives from TriggerButton above: same border token, same
      // surface, same radius — the panel used to carry a heavier
      // --border-medium edge over a different surface than the field it
      // belongs to.
      className={cn("border border-(--border-subtle) bg-(--surface-secondary) shadow-[var(--shadow-md)] rounded-(--radius-md) overflow-hidden")}
      style={dropdownPanelStyle(pos, 10050)}>
      <div className="max-h-60 overflow-auto">
        {searchable ? searchInput : null}
        {optionsList}
      </div>
    </div>,
    document.body,
  );
}
